// Extensions keep their refresh token in extension storage; the web app never
// sees it because it lives in an httpOnly cookie.
const KEY = "unideck.auth.refreshToken";

type StorageArea = {
  get: (
    keys: string,
    callback?: (items: Record<string, unknown>) => void,
  ) => unknown;
  set: (items: Record<string, unknown>, callback?: () => void) => unknown;
  remove: (keys: string, callback?: () => void) => unknown;
};

const extensionArea = (): { area: StorageArea; promises: boolean } | null => {
  const scope = globalThis as unknown as {
    browser?: { storage?: { local?: StorageArea } };
    chrome?: { storage?: { local?: StorageArea } };
  };
  if (scope.browser?.storage?.local) {
    return { area: scope.browser.storage.local, promises: true };
  }
  if (scope.chrome?.storage?.local) {
    return { area: scope.chrome.storage.local, promises: false };
  }
  return null;
};

export const refreshStorage = {
  async get(): Promise<string | null> {
    const target = extensionArea();
    if (!target) {
      return localStorage.getItem(KEY);
    }
    const items = target.promises
      ? ((await target.area.get(KEY)) as Record<string, unknown>)
      : await new Promise<Record<string, unknown>>(resolve =>
          target.area.get(KEY, resolve),
        );
    const value = items?.[KEY];
    return typeof value === "string" ? value : null;
  },

  async set(token: string): Promise<void> {
    const target = extensionArea();
    if (!target) {
      localStorage.setItem(KEY, token);
      return;
    }
    if (target.promises) {
      await target.area.set({ [KEY]: token });
    } else {
      await new Promise<void>(resolve =>
        target.area.set({ [KEY]: token }, resolve),
      );
    }
  },

  async clear(): Promise<void> {
    localStorage.removeItem(KEY);
    const target = extensionArea();
    if (!target) {
      return;
    }
    if (target.promises) {
      await target.area.remove(KEY);
    } else {
      await new Promise<void>(resolve => target.area.remove(KEY, resolve));
    }
  },
};
